/**
 * shared/space_ops.ts — CLIENT MIRROR of server/src/lib/space_ops.ts (the
 * server copy is authoritative; keep them byte-identical below the headers).
 * Space Wave 7 — organisations plan, individuals enact: the civ operations
 * and their bounded utilities, the fleet stances, the firms' policies. Pure.
 */
import { warH } from './space_stations';
import { BATTLE_PERIOD_S, RESERVE_FLOOR, BATTLE_CAPS } from './space_campaign';

// ── THE DECISION LAW (Space Wave 7 — organisations plan, individuals enact) ──
export const CIV_OPERATIONS = ['press', 'hold', 'rebuild', 'escort', 'truce'] as const;
export type CivOperation = (typeof CIV_OPERATIONS)[number];
export const OPERATION_LABEL: Record<CivOperation, string> = {
  press: 'PRESS — carry the next war to a chosen front',
  hold: 'HOLD — keep the line, reserve two capitals',
  rebuild: 'REBUILD — keep the yard\'s capitals home, reserve half the store',
  escort: 'ESCORT — pay the lane\'s escorts double',
  truce: 'TRUCE — propose (or accept) a ceasefire',
};
/** A decision is taken once per war cycle; the campaign of that cycle reads it. */
export function decisionCycle(nowS: number): number { return Math.floor(nowS / BATTLE_PERIOD_S); }
/** Capitals a civ keeps back from the next campaign's commit. */
export const RESERVATION: Record<CivOperation, (capitals: number) => number> = {
  press: () => 0,
  hold: () => 2,
  rebuild: (c) => Math.ceil(c / 2),
  escort: () => 0,
  truce: () => 0,
};
export const ESCORT_REWARD_MULT_ESCORT = 2;
export const ADVICE_BIAS = 0.5;
export const CEASEFIRE_CYCLES = 3;
export const TREATY_MIN_TERM_CYCLES = 2;
export const GRUDGE_CYCLES = 6;
export const LANE_LOSS_WINDOW_H = 24;

export interface CivState {
  hulls: number; capitals: number;
  /** The civ's lane runs lost / arrived inside LANE_LOSS_WINDOW_H. */
  runsLost: number; runsArrived: number;
  /** +1 the civ won the last campaign, −1 it lost, 0 none / a draw. */
  lastWar: -1 | 0 | 1;
  /** Mean control score (−100..100, positive = this civ) over the contested systems it could press. */
  control: number;
  /** A ceasefire stands with the other civ (any state but expired/breached). */
  treaty: boolean;
  /** Cycles since the other civ breached a treaty against us (−1 = never). */
  grudgeAgo: number;
  /** The advice on the table for this cycle, if any. */
  advice: CivOperation | null;
}
export type Utilities = Record<CivOperation, number>;
const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
/** Bounded utilities in [0, 1] — every input is read through a clamp, so no store, loss or grudge can escape the box. */
export function civUtilities(s: CivState): Utilities {
  const capStrength = clamp01(s.capitals / (2 * BATTLE_CAPS));          // 20 capitals = full strength
  const hullStrength = clamp01(s.hulls / (2 * RESERVE_FLOOR.hulls));
  const weak = clamp01((RESERVE_FLOOR.capitals - s.capitals) / RESERVE_FLOOR.capitals); // 1 at zero capitals
  const runs = s.runsLost + s.runsArrived;
  const laneLoss = runs > 0 ? clamp01(s.runsLost / runs) : 0;
  const losing = s.control < 0 ? clamp01(-s.control / 100) : 0;
  const winning = s.control > 0 ? clamp01(s.control / 100) : 0;
  const grudge = s.grudgeAgo >= 0 && s.grudgeAgo < GRUDGE_CYCLES ? 1 - s.grudgeAgo / GRUDGE_CYCLES : 0;
  const u: Utilities = {
    press: clamp01(0.25 + 0.45 * capStrength + 0.15 * (s.lastWar > 0 ? 1 : 0) + 0.2 * losing + 0.4 * grudge - 0.6 * weak - (s.treaty && !grudge ? 0.35 : 0)),
    hold: clamp01(0.3 + 0.3 * winning + 0.2 * (s.lastWar < 0 ? 1 : 0) - 0.2 * capStrength),
    rebuild: clamp01(0.1 + 0.8 * weak + 0.3 * (1 - hullStrength)),
    escort: clamp01(0.1 + 0.9 * laneLoss),
    truce: clamp01(0.05 + 0.5 * weak + 0.25 * (s.lastWar < 0 ? 1 : 0) - 0.3 * grudge - (s.treaty ? 0.5 : 0)),
  };
  if (s.advice) u[s.advice] = clamp01(u[s.advice] + ADVICE_BIAS);
  return u;
}
/** The operation with the highest utility; ties fall to the listed order (press first). */
export function chooseOperation(u: Utilities): CivOperation {
  let best: CivOperation = CIV_OPERATIONS[0];
  for (const k of CIV_OPERATIONS) if (u[k] > u[best] + 1e-9) best = k;
  return best;
}
/** Where a PRESS carries the war: the contested system the civ is weakest in (a front it wants back), by control score. */
export function pressTargetOf(control: ReadonlyArray<{ systemId: string; score: number }>, side: 0 | 1, cyc: number): string | null {
  if (!control.length) return null;
  const sgn = side === 0 ? 1 : -1;
  let best = control[0], bestV = Infinity;
  for (const c of control) { const v = c.score * sgn; if (v < bestV) { bestV = v; best = c; } }
  void cyc;
  return best.systemId;
}

// ── FLEET COMMAND: the stance ────────────────────────────────────────────────
export const STANCES = ['assault', 'screen', 'withdraw'] as const;
export type Stance = (typeof STANCES)[number];
export const STANCE_ROUNDS = 5;
/** What a stance does to a round: the losses WE take and the losses we INFLICT, and the retreat margin. */
export const STANCE_FACTORS: Record<Stance, { take: number; give: number; retreatMargin: number }> = {
  assault: { take: 1.3, give: 1.4, retreatMargin: 0 },
  screen: { take: 0.7, give: 0.7, retreatMargin: 0 },
  withdraw: { take: 0.5, give: 0.4, retreatMargin: 1 },
};
export function chooseStance(ours: number, theirs: number, reserveCaps: number, round: number): Stance {
  if (ours <= 0) return 'withdraw';
  const ratio = ours / Math.max(1, theirs);
  if (ratio < 0.6 && reserveCaps < RESERVE_FLOOR.capitals) return 'withdraw';
  if (ratio >= 1.25 || (round === 0 && ratio >= 1)) return 'assault';
  return 'screen';
}

// ── THE FIRM'S POLICY ────────────────────────────────────────────────────────
export type FirmPolicy = { runs: boolean; escortMult: number; reason: string };
export const FIRM_PAUSE_LOSSES = 2;
export function firmPolicyFor(runsLost: number, runsArrived: number, civEscort: boolean): FirmPolicy {
  if (runsLost >= FIRM_PAUSE_LOSSES && runsLost > runsArrived) return { runs: false, escortMult: 2, reason: `${runsLost} runs lost in ${LANE_LOSS_WINDOW_H} h — the lane waits for escorts` };
  if (civEscort) return { runs: true, escortMult: ESCORT_REWARD_MULT_ESCORT, reason: 'the civ funds the lane\'s escorts' };
  if (runsLost > 0) return { runs: true, escortMult: 1.5, reason: `${runsLost} run${runsLost > 1 ? 's' : ''} lost — a raised escort reward` };
  return { runs: true, escortMult: 1, reason: 'the lane is quiet' };
}
/** A stable hash for a decision's tie-break or a treaty's id (unused inputs keep the signature honest). */
export function decisionSalt(civ: string, cyc: number): number { return warH(`ops|${civ}|${cyc}`); }
